import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Button,
  Chip
} from '@mui/material';
import { THEME_COLORS, TYPOGRAPHY, ANIMATIONS } from '../constants';
import { formatDate } from '../utils';

/**
 * Composant pour la sélection d'activité
 */
const ActivitySelector = ({
  activities,
  selectedActivity,
  loading,
  error,
  onActivityChange,
  onRetry
}) => {
  return (
    <Box sx={{ mb: { xs: 3, sm: 4 } }}>
      {/* Section header responsive */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Typography 
          variant="h6"
          sx={{ 
            fontFamily: TYPOGRAPHY.fontFamily.primary,
            fontWeight: TYPOGRAPHY.fontWeight.semibold,
            color: THEME_COLORS.text.primary,
            fontSize: { xs: TYPOGRAPHY.fontSize.lg, sm: TYPOGRAPHY.fontSize.lg },
            mb: { xs: 0.5, sm: 1 }
          }}
        >
          Choisir une activité
        </Typography>
        <Typography 
          variant="body2"
          sx={{ 
            color: THEME_COLORS.text.secondary,
            fontFamily: TYPOGRAPHY.fontFamily.primary,
            fontSize: { xs: TYPOGRAPHY.fontSize.sm, sm: TYPOGRAPHY.fontSize.base }
          }}
        >
          Sélectionnez l'événement ou la conférence qui vous intéresse
        </Typography>
      </Box>

      {/* Gestion des erreurs modernes */}
      {error && (
        <Alert 
          severity="error"
          variant="filled"
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={onRetry}
              sx={{
                fontFamily: TYPOGRAPHY.fontFamily.primary,
                fontWeight: TYPOGRAPHY.fontWeight.medium
              }}
            >
              Réessayer
            </Button>
          }
          sx={{
            mb: 3,
            borderRadius: 3,
            fontFamily: TYPOGRAPHY.fontFamily.primary,
            '& .MuiAlert-icon': {
              fontSize: '1.2rem'
            }
          }}
        >
          {error}
        </Alert>
      )}

      {/* Sélecteur moderne */}
      <FormControl fullWidth>
        <InputLabel 
          id="activity-select-label"
          sx={{
            fontFamily: TYPOGRAPHY.fontFamily.primary,
            fontWeight: TYPOGRAPHY.fontWeight.medium,
            '&.Mui-focused': {
              color: THEME_COLORS.primary.main
            }
          }}
        >
          {loading ? '🔄 Chargement des activités...' : '📅 Sélectionner une activité'}
        </InputLabel>
        <Select
          labelId="activity-select-label"
          value={selectedActivity}
          label={loading ? '🔄 Chargement...' : '📅 Sélectionner une activité'}
          onChange={(e) => onActivityChange(e.target.value)}
          disabled={loading}
          sx={{
            borderRadius: { xs: 2, sm: 3 },
            fontFamily: TYPOGRAPHY.fontFamily.primary,
            transition: ANIMATIONS.transition.normal,
            fontSize: { xs: TYPOGRAPHY.fontSize.sm, sm: TYPOGRAPHY.fontSize.base },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: THEME_COLORS.border.main,
              borderWidth: '1px'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: THEME_COLORS.primary.light,
              borderWidth: '1.5px'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: THEME_COLORS.primary.main,
              borderWidth: '2px',
              boxShadow: `0 0 0 2px ${THEME_COLORS.primary.main}15`
            },
            '& .MuiSelect-select': {
              py: { xs: 1.5, sm: 2 }
            }
          }}
        >
          {activities
            .filter((activity) => {
              // Filtrer les activités dont la date est strictement antérieure à la date du jour
              if (!activity.date) return false;
              const activityDate = new Date(activity.date);
              const today = new Date();
              // Réinitialiser l'heure à 00:00:00 pour comparer seulement les dates
              today.setHours(0, 0, 0, 0);
              activityDate.setHours(0, 0, 0, 0);
              return activityDate >= today;
            })
            .map((activity) => (
            <MenuItem 
              key={activity.id} 
              value={activity.id}
              sx={{
                py: { xs: 1.5, sm: 2 },
                px: { xs: 2, sm: 3 },
                borderRadius: { xs: 1, sm: 2 },
                mx: { xs: 0.5, sm: 1 },
                mb: 0.5,
                transition: ANIMATIONS.transition.fast,
                '&:hover': {
                  backgroundColor: THEME_COLORS.primary.main + '08',
                  transform: { xs: 'none', sm: 'translateX(2px)' }
                },
                '&.Mui-selected': {
                  backgroundColor: THEME_COLORS.primary.main + '12',
                  '&:hover': {
                    backgroundColor: THEME_COLORS.primary.main + '20'
                  }
                }
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1, sm: 0 },
                  mb: { xs: 0.5, sm: 1 }
                }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: TYPOGRAPHY.fontWeight.semibold,
                      fontFamily: TYPOGRAPHY.fontFamily.primary,
                      color: THEME_COLORS.text.primary,
                      fontSize: { xs: TYPOGRAPHY.fontSize.sm, sm: TYPOGRAPHY.fontSize.sm },
                      lineHeight: 1.3
                    }}
                  >
                    {activity.name}
                  </Typography>
                  <Chip
                    label={activity.category || 'Salon/Conférence'}
                    size="small"
                    sx={{
                      backgroundColor: THEME_COLORS.secondary.main + '15',
                      color: THEME_COLORS.secondary.main,
                      fontFamily: TYPOGRAPHY.fontFamily.primary,
                      fontWeight: TYPOGRAPHY.fontWeight.medium,
                      fontSize: { xs: '0.6rem', sm: TYPOGRAPHY.fontSize.xs },
                      borderRadius: { xs: 1, sm: 2 },
                      height: { xs: 20, sm: 24 }
                    }}
                  />
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: THEME_COLORS.text.secondary,
                    fontFamily: TYPOGRAPHY.fontFamily.primary,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: { xs: TYPOGRAPHY.fontSize.xs, sm: TYPOGRAPHY.fontSize.xs }
                  }}
                >
                  📅 {formatDate(activity.date)}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default ActivitySelector;